import { Button, Dialog, DialogBody, DialogContent, DialogFooter, DialogTrigger } from '@civilgrid/ui/components';
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useSnapshot } from 'valtio';
import { submitDownloadRequest } from '~/api/projects2';
import { getReasonForError } from '~/common/getReasonForError';
import { captureTypedPosthogEvent, type ExportEntryPoint, POSTHOG_EVENTS } from '~/common/posthogEvents';
import Notify from '~/components2/Notify';
import { DOWNLOAD_LABELS, type ExportLabels } from '~/hooks/useExportTerminologyEnabled';
import rootStore from '~/store';
import { TOCLabelMap, TOCLegendParentMap } from '~/store2/TOCLayers';
import { EXPORT_HISTORY_TAB_ID } from '../exportTabIds';
import { fileFormats } from '../utilities/common';
import store from './store';

// Normalize label/short/value to the canonical short code so PostHog funnels don't split.
function normalizeExportType(input: string): string {
  const match = fileFormats.find((f) => f.short === input || f.label === input || f.value === input);
  return match?.short ?? input.toLowerCase();
}

interface ConfirmSubmitRequestProps {
  projection: string;
  fileFormat: string;
  labels: ExportLabels;
}

const ConfirmSubmitRequestContent = ({ projection, fileFormat, labels }: ConfirmSubmitRequestProps) => {
  const { selectedLayerIds } = useSnapshot(store);
  return (
    <div className="items-left flex flex-col space-y-2">
      <div className="text-base">{labels.requestDialogIntro}</div>
      <div className="flex flex-col space-y-2">
        <div className="font-bold">File Format</div>
        <div>{fileFormat}</div>
        <div className="font-bold">Projection</div>
        <div>{projection}</div>
        <div className="font-bold">Layers</div>
        <div className="flex flex-col space-y-1">
          <div>
            {Array.from(selectedLayerIds)
              .map((layerId) => TOCLegendParentMap.get(layerId) ?? TOCLabelMap.get(layerId) ?? layerId)
              .join(', ')}
          </div>
        </div>
      </div>
    </div>
  );
};

type SubmitRequestDialogProps = {
  projectId: string;
  projection: string;
  fileFormat: string;
  downloadArea: number;
  /** PostHog `source` for the submitted-request event. Defaults to the legacy value. */
  source?: string;
  /**
   * Copy for the dialog and its toasts. Defaults to download wording because the legacy ESRI form
   * renders this too and is excluded from the rename; the FME form passes flag-gated labels.
   */
  labels?: ExportLabels;
  /**
   * Real format/projection/template values from the multi-format extract flow. Falls back to
   * `[fileFormat]`/`projection` for the legacy single-format caller, which has no equivalents.
   */
  formats?: string[];
  projectionId?: string;
  extractTemplateId?: string | null;
  /** How the user reached the extract tab; the legacy form has none. */
  entryPoint?: ExportEntryPoint;
};

export const SubmitRequestDialog = ({
  projectId,
  projection,
  fileFormat,
  downloadArea,
  source = 'download_request',
  labels = DOWNLOAD_LABELS,
  formats,
  projectionId,
  extractTemplateId,
  entryPoint,
}: SubmitRequestDialogProps) => {
  const navigate = useNavigate();
  const { selectedLayerIds, project } = useSnapshot(store);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [open, setOpen] = useState(false);

  const layers = Array.from(selectedLayerIds);

  const handleSubmitRequest = async () => {
    try {
      const response = await submitDownloadRequest({
        projectId,
        projectionId: projectionId ?? projection,
        formats: formats ?? [fileFormat],
        layers,
        extractTemplateId,
      });
      if (response.success === true) {
        Notify.info({
          message: labels.requestSuccessToast,
        });
        captureTypedPosthogEvent(POSTHOG_EVENTS.projectExportInitiated, {
          project_id: projectId,
          organization_id: rootStore.currentOrganizationId || undefined,
          export_type: normalizeExportType(fileFormat),
          source,
          entry_point: entryPoint,
        });

        navigate({ to: '/projects/$projectId/$tabId', params: { projectId, tabId: EXPORT_HISTORY_TAB_ID } });
      } else {
        captureTypedPosthogEvent(POSTHOG_EVENTS.projectExportFailed, {
          project_id: projectId,
          organization_id: rootStore.currentOrganizationId || undefined,
          reason: 'api_error',
        });
        Notify.error({
          message: labels.requestErrorToast,
        });
      }
    } catch (err) {
      captureTypedPosthogEvent(POSTHOG_EVENTS.projectExportFailed, {
        project_id: projectId,
        organization_id: rootStore.currentOrganizationId || undefined,
        reason: getReasonForError(err),
      });
      Notify.error({
        title: '⚠️',
        message: labels.requestErrorToast,
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button disabled={selectedLayerIds.size === 0} data-testid="new-download-submit-request-trigger">
          Submit Request
        </Button>
      </DialogTrigger>
      <DialogContent
        title={labels.requestDialogTitle}
        className="z-[110] w-auto min-w-min max-w-[75vw]"
        overlayClassName="z-[100]"
        data-testid="new-download-submit-request-dialog"
      >
        <DialogBody className="text-sm">
          <ConfirmSubmitRequestContent projection={projection} fileFormat={fileFormat} labels={labels} />
        </DialogBody>
        <DialogFooter>
          <Button
            data-testid="new-download-submit-request-confirm"
            // Awaited and gated so repeated clicks can't fire several requests for one submission.
            disabled={isSubmitting}
            loading={isSubmitting}
            onClick={async () => {
              setIsSubmitting(true);
              try {
                await handleSubmitRequest();
              } finally {
                setIsSubmitting(false);
              }
              setOpen(false);
            }}
          >
            Submit Request
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
